import styled, { css } from 'styled-components';
import { Lock } from '@mui/icons-material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';

import colors from '../../../../../styles/colors';
import { DirectionRow } from '../../../../../styles/grid';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin-top: 15px;
`;

const ErrorMessage = styled.div`
  font-size: 14px;
  color: ${colors.base.danger};
  margin-bottom: 10px;
`;

const stripeFieldStyles = css`
  width: ${(props) => (props.width ? props.width : '100%')};
  font-size: 16px;
  border-radius: 5px;
  padding: 8px 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;

  &.StripeElement {
    color: ${colors.shades.dark4};
  }
`;

const StripeCardNumberElement = styled(CardNumberElement)`
  ${stripeFieldStyles}
`;

const StripeCardExpiryElement = styled(CardExpiryElement)`
  ${stripeFieldStyles}
`;

const StripeCardCvcElement = styled(CardCvcElement)`
  ${stripeFieldStyles}
`;

/**
 * Note : Each Div element has a margin of 16px to the right, except for the last div child.
 */
const InputFieldGroup = styled(DirectionRow)`
  width: 100%;
  margin-bottom: 10px;
  flex-direction: column;
  row-gap: 20px;
  justify-content: 'flex-start';
`;

const CheckoutContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ElementContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 1px solid ${colors.shades.gamma14};
  background-color: ${({ $isDarkTheme, $isFieldEmpty }) => {
    if ($isDarkTheme) {
      return $isFieldEmpty ? colors.shades.dark5 : colors.shades.dark3;
    }
    return $isFieldEmpty ? colors.base.alpha : colors.shades.alpha2;
  }};
  border-radius: 5px;
  max-width: 450px; 
  padding: 10px 20px;
`;

const Label = styled.label`
  color: ${colors.shades.gamma14};
`;

const BottomTextContainer = styled.div`
  display: flex;
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.gamma14 : colors.shades.dark4)};
  font-size: 12px;
  justify-content: center;
  margin-top: 15px;
`;

const StyledLockIcon = styled(Lock)`
  margin-right: 5px;
  width: 15px;
  height: 15px;
`;

const DoubleElementContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: space-between;
  width: 100%;
`;

const PaymentSummary = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 100px;
`;

const PaymentSummaryTitle = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  padding: 20px 0 10px 0;
  border-bottom: 1px solid ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.gamma3)};
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark4 : colors.shades.dark2)};
`;

const PaymentContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 15px;
`;

const PaymentSummaryContent = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.alpha : colors.base.beta)};
`;

const PaymentSummarySubContent = styled.div`
  display: flex;
  color: ${({ $color }) => $color || colors.shades.gamma10};
  font-size: 12px;
  margin-top: 5px;
`;

const TextInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-size: 16px;
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.alpha : colors.base.beta)};
  padding: 3px 0;

  &::placeholder {
    color: ${colors.shades.gamma14};
  }
`;

const CountrySelect = styled.select`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-size: 16px;
  color: ${({ $isDarkTheme, $isEmpty }) => {
    if ($isEmpty) return colors.shades.gamma14;
    return $isDarkTheme ? colors.base.alpha : colors.base.beta;
  }};
  padding: 3px 0;
  appearance: none;
  cursor: pointer;

  option {
    color: ${colors.base.beta};
    background: ${colors.base.alpha};
  }
`;

export {
  Container,
  CheckoutContent,
  ErrorMessage,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  InputFieldGroup,
  ElementContainer,
  Label,
  BottomTextContainer,
  StyledLockIcon,
  DoubleElementContainer,
  PaymentSummary,
  PaymentSummaryTitle,
  PaymentSummaryContent,
  PaymentContentContainer,
  PaymentSummarySubContent,
  TextInput,
  CountrySelect,
};
